import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { forEach, get, isEmpty, isNil, last } from 'lodash';
import { NavPath } from 'src/pages/NavigationShell/navigationUtils';
import { current } from 'immer';

export interface BreadcrumbRestore {
  /** restores selection and vertical scroll position in grid */
  selectedGridRowId: string | null;
  /** restores selection and horizontal scroll position in grid */
  selectedGridColumnId: string | null;
  /** restores selection and scrollTo in companion */
  selectedCompanionId: string | null;
  /** companion level selection */
  selectedLevel: string | null;
}

export interface CrumbPath {
  /**
   * generated unique identifier,
   * this will help maintain order in case of duplicate paths names (self linking)
   * */
  crumbId: string;
  /** original path */
  path: NavPath;
  queryParams: string;
  url: string;
  // snapshot of view state to be applied when returning to a breadcrumb link
  restore: BreadcrumbRestore;
}
export interface BreadcrumbState {
  crumbPaths: Record<string, CrumbPath[]>;
  activeBreadcrumbKey: string | null;
  activeBreadcrumbRestore: BreadcrumbRestore | null;
}

export interface AddBreadcrumbPayload {
  breadcrumbKey: string;
  currentCrumbPath: CrumbPath;
  nextCrumbPath: CrumbPath;
}

const initialState: BreadcrumbState = {
  crumbPaths: {},
  activeBreadcrumbKey: null,
  activeBreadcrumbRestore: null,
};

const breadcrumbsSlice = createSlice({
  name: 'breadcrumbs',
  initialState: initialState,
  reducers: {
    addCrumb: (state, action: PayloadAction<AddBreadcrumbPayload>) => {
      const { breadcrumbKey, currentCrumbPath, nextCrumbPath } = action.payload;

      // need to get a normal object from immer instead of a
      // writable draft obj to use in non-immer functions (i.e. lodash)
      const crumbPathsCopy = current(state.crumbPaths);
      const curActiveBread = state.activeBreadcrumbKey;

      // when necessary, add `current` path (starting point of breadcrumbs) to existing paths
      // otherwise things like "isEmpty" fail due to looking at the draft rather than the actual object/array
      let nextCrumbPaths: CrumbPath[];
      if (curActiveBread == null || isEmpty(crumbPathsCopy[curActiveBread])) {
        nextCrumbPaths = [currentCrumbPath, nextCrumbPath];
      } else {
        const curStatePaths = crumbPathsCopy[curActiveBread] ?? [];
        nextCrumbPaths = [...curStatePaths, nextCrumbPath];
      }

      state.activeBreadcrumbRestore = null;
      state.activeBreadcrumbKey = breadcrumbKey;
      state.crumbPaths[breadcrumbKey] = nextCrumbPaths;
    },
    removeCrumbsAfterId: (state, action: PayloadAction<CrumbPath>) => {
      const activeCrumb = state.activeBreadcrumbKey;
      if (!isNil(activeCrumb)) {
        // the selected CrumbPath matching crumbId will be active
        const nextActiveCrumbId = action.payload.crumbId;

        // need to get a normal object from immer instead of a
        // writable draft obj to use in non-immer functions (i.e. lodash)
        const crumbPathsCopy = current(state.crumbPaths[activeCrumb]);

        // find the next active crumb index, it will be the new end of the array
        const nextActiveCrumbIndex = crumbPathsCopy.findIndex((cpo) => cpo.crumbId === nextActiveCrumbId);
        const nextActiveCrumb = get(crumbPathsCopy, nextActiveCrumbIndex);

        // remove items after the nextActiveCrumb
        const nextCrumbs = crumbPathsCopy.slice(0, nextActiveCrumbIndex + 1);

        // when there is 1 item the root breadcrumb is active, so empty breadcrumb state
        const needsReset = nextCrumbs.length == 0 || nextCrumbs.length == 1;
        if (needsReset) {
          // back on root crumb, reset crumb key state
          // don't remove activeCrumbScrollToId yet because we may need for the current view we are on
          state.crumbPaths[activeCrumb] = [];
          state.activeBreadcrumbKey = null;
        } else {
          state.crumbPaths[activeCrumb] = nextCrumbs;
        }

        state.activeBreadcrumbRestore = {
          selectedCompanionId: nextActiveCrumb?.restore.selectedCompanionId ?? null,
          selectedGridRowId: nextActiveCrumb?.restore.selectedGridRowId ?? null,
          selectedGridColumnId: nextActiveCrumb?.restore.selectedGridColumnId ?? null,
          selectedLevel: nextActiveCrumb?.restore.selectedLevel ?? null,
        };
      }
    },
    removeLastCrumb: (state) => {
      const activeCrumb = state.activeBreadcrumbKey;

      if (!isNil(activeCrumb)) {
        const crumbPathsCopy = current(state.crumbPaths[activeCrumb]);
        crumbPathsCopy.pop();
        const nextActiveCrumb = last(crumbPathsCopy);
        if (crumbPathsCopy.length === 1) {
          // back on root crumb, reset state all the way
          state.crumbPaths[activeCrumb] = [];
          state.activeBreadcrumbKey = null;
        } else {
          state.crumbPaths[activeCrumb] = crumbPathsCopy;
        }

        state.activeBreadcrumbRestore = {
          selectedCompanionId: nextActiveCrumb?.restore.selectedCompanionId ?? null,
          selectedGridRowId: nextActiveCrumb?.restore.selectedGridRowId ?? null,
          selectedGridColumnId: nextActiveCrumb?.restore.selectedGridColumnId ?? null,
          selectedLevel: nextActiveCrumb?.restore.selectedLevel ?? null,
        };
      }
    },
    resetCrumbsByKey: (state, action: PayloadAction<string>) => {
      state.crumbPaths[action.payload] = [];
      state.activeBreadcrumbKey = null;
      state.activeBreadcrumbRestore = null;
    },
    resetAllCrumbs: (state) => {
      forEach(state.crumbPaths, (_, crumbKey) => {
        state.crumbPaths[crumbKey] = [];
      });
      state.activeBreadcrumbKey = null;
      state.activeBreadcrumbRestore = null;
    },
    clearActiveRestore: (state) => {
      // clear only the top level storage,
      // each individual breadcrumb still retains their own scrollToId
      state.activeBreadcrumbRestore = null;
    },
  },
});

export const { addCrumb, removeCrumbsAfterId, removeLastCrumb, resetAllCrumbs, resetCrumbsByKey, clearActiveRestore } =
  breadcrumbsSlice.actions;

export default breadcrumbsSlice.reducer;
